import React from 'react';
import { Check } from 'lucide-react';
import './VerifiedBadge.css';

export interface VerifiedBadgeProps {
  /** Whether the document is currently marked as verified. */
  verified: boolean;
  /** Optional click handler — opens the confirm dialog. Omit to render read-only. */
  onClick?: () => void;
  /** Tooltip shown on hover (typically "Verified by <email> · <date>"). */
  tooltip?: string;
  /** Aria label override. Defaults to a German-localised pair. */
  ariaLabel?: string;
  /** Optional class name */
  className?: string;
}

/**
 * Toggleable verification pill rendered in the document header (#495).
 *
 * Two visual states:
 *   - unverified: light-grey check + "Verified" label, hover highlights
 *   - verified:   light-green pill + check + "Verified" label
 *
 * Click invokes onClick (caller is responsible for the confirm-then-toggle flow).
 */
export const VerifiedBadge: React.FC<VerifiedBadgeProps> = ({
  verified,
  onClick,
  tooltip,
  ariaLabel,
  className = '',
}) => {
  const label = ariaLabel ?? (verified ? 'Verifiziert. Klick zum Aufheben.' : 'Nicht verifiziert. Klick zum Verifizieren.');
  const stateClass = verified ? 'verified-badge--on' : 'verified-badge--off';
  const interactiveClass = onClick ? 'verified-badge--interactive' : '';

  return (
    <button
      type="button"
      className={`verified-badge ${stateClass} ${interactiveClass} ${className}`.trim()}
      onClick={onClick}
      disabled={!onClick}
      aria-pressed={verified}
      aria-label={label}
      title={tooltip}
      data-testid="verified-badge"
    >
      <Check size={14} className="verified-badge__icon" data-testid="verified-badge-icon" aria-hidden="true" />
      <span className="verified-badge__text">Verified</span>
    </button>
  );
};

export default VerifiedBadge;
